import { yupResolver } from '@hookform/resolvers/yup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import { useCreateTDPMutation } from 'api/tdp';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import Tooltip from 'components/ui/tooltip';
import path from 'constants/path';
import { useDebounce } from 'hooks';
import _intersection from 'lodash/intersection';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import { ArrowRight, ChevronDown, Plus } from 'lucide-react';
import { showSuccessMessage, showUncaughtErrorMessage } from 'modules/alert/utils';
import { useRelatedThreadsQuery } from 'modules/relatedThreads/relatedThreadsApi';
import { useTDPTemplatesQuery } from 'modules/tdp/tdpTemplateApi';
import { SEARCH_THREADS_PARAMS } from 'modules/threads/constants';
import { useThreadsList } from 'modules/threads/hooks';
import {
  useGetSearchThreadsQuery,
  useGetThreadsByRelatedThreadsQuery,
  useGetThreadTagsQuery,
} from 'modules/threads/threadsApi';
import { IThreadsResult } from 'modules/threads/types';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import colors from 'theme/colors';
import { TReactSelectOption } from 'types';
import { ReactSelect, TagBadge, Text } from 'ui';
import { createOption } from 'utils/helpers';
import { validationSchema } from 'utils/validation';
import _isEmpty from 'lodash/isEmpty';
import { InferType, number, object, string } from 'yup';
import { useIsMobile } from 'hooks/useIsMobile';

const validation = object({
  name: validationSchema.stringRequired(100, 'You must enter a valid name.'),
  primaryThread: number().required().min(1, 'You must select a primary thread.'),
  templateId: string().required('You must select a template.'),
  threadsGroup: string(),
});

interface Props {
  /** Existing primary thread ID passed in from dialog referer. */
  selectedThread?: string | null;
  trigger?: React.ReactNode;
}

interface IGroupSummaryRowProps {
  thread: IThreadsResult;
  listIndex: number;
  tagsList: JSX.Element | null;
}

const GroupSummaryThreadRow = ({ thread, listIndex, tagsList }: IGroupSummaryRowProps) => {
  return (
    <li key={thread.id} className="my-3 flex items-center px-2">
      <span className="mr-2 text-app-gray">{listIndex}.</span>
      <div className="flex w-full items-center justify-between gap-4">
        <Link target="_blank" to={`/thread/${thread.id}/`}>
          <span
            style={{ color: colors.blue7 }}
            className="flex items-center gap-1 break-all underline"
          >
            <Text color={colors.blue7}>{thread.name}</Text> <ArrowRight size={15} />
          </span>
        </Link>
        {tagsList}
      </div>
    </li>
  );
};

const AddTDPDialog: React.FC<Props> = ({ selectedThread, trigger }) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<TReactSelectOption[] | []>([]);
  const selectedTagLabels = useMemo(() => _map(selectedTags, 'label'), [selectedTags]);
  const isMobile = useIsMobile();

  const form = useForm({
    defaultValues: {
      name: '',
      primaryThread: selectedThread ? Number.parseInt(selectedThread, 10) : 0,
      templateId: '',
      threadsGroup: '',
    },
    resolver: yupResolver(validation),
  });

  const { primaryThread: selectedPrimaryThreadId } = form.watch();
  const { data: relatedThreadResponse } = useRelatedThreadsQuery(String(selectedPrimaryThreadId), {
    skip: !open,
  });
  const relatedThreads = relatedThreadResponse?.results;
  const relatedThreadIds: number[] = useMemo(
    () => _map(relatedThreads, 'thread.id'),
    [relatedThreads]
  );
  const debouncedSearch = useDebounce(searchValue, 250);

  const { data: threadTags } = useGetThreadTagsQuery('');
  const { data: templates } = useTDPTemplatesQuery({ limit: 9999 });

  // This data will populate the "Primary Thread" dropdown list.
  const { data: searchThreads } = useGetSearchThreadsQuery(
    {
      limit: SEARCH_THREADS_PARAMS.LIMIT,
      offset: SEARCH_THREADS_PARAMS.OFFSET,
      search: debouncedSearch,
    },
    { skip: !open }
  );

  const shouldSkipQueryRelatedThreads =
    !open || // Skip if the modal is not open.
    !selectedPrimaryThreadId; // If no primary thread is selected.

  /**
   * Handle fetch of related threads, and threads who are related to selected primary thread.
   * This will populate the accordion/pop-down ("Threads Group Summary") view.
   */
  const { data: threadsByRelatedThreadResponse } = useGetThreadsByRelatedThreadsQuery(
    {
      relatedThreadIds: [selectedPrimaryThreadId],
    },
    {
      skip: shouldSkipQueryRelatedThreads,
    }
  );

  // Exclude the primary thread from the threads list, to be displayed in the "Threads Group Summary" view.
  const threadsByRelatedThreadsList = _reject(threadsByRelatedThreadResponse?.results, {
    id: selectedPrimaryThreadId,
  });

  /**
   * Handle fetch of threads by tags.
   * This will also populate the accordion/pop-down ("Threads Group Summary") view.
   */
  const shouldSkipQueryThreadsByTags = !open || _isEmpty(selectedTags);
  const { data: threadsByTagsResponse } = useThreadsList({
    url: path.allThreadsList,
    queryFilters: {
      tags: selectedTagLabels,
    },
    shouldSkipRequest: shouldSkipQueryThreadsByTags,
  });
  const threadsByTagsList = threadsByTagsResponse?.results || [];

  /** Combination of related threads and threads filtered by tags. */
  const threadsGroupSummaryList = [...threadsByRelatedThreadsList, ...threadsByTagsList];

  const tagOptions: TReactSelectOption[] = useMemo(
    () => threadTags?.map((tag) => createOption(tag)) || [],
    [threadTags]
  );

  const threadsOptions = useMemo<TReactSelectOption[]>(() => {
    return (
      searchThreads?.results.map((thread) => ({
        label: thread.name,
        value: thread.id.toString(),
      })) || []
    );
  }, [searchThreads]);

  const [createTDP, { isLoading }] = useCreateTDPMutation();

  const navigate = useNavigate();

  const onSubmit = async (values: InferType<typeof validation>) => {
    try {
      const response = await createTDP({
        name: values.name,
        primaryThread: values.primaryThread,
        template: Number.parseInt(values.templateId, 10),
        // Pass the Threads Tags values that user selected in the input field.
        relatedThreadIds: selectedTags.map((tag) => tag.value),
      }).unwrap();
      navigate(`/summary-reports/${response.id}`);
      showSuccessMessage('Summary Report created');
      setOpen(false);
    } catch (error) {
      showUncaughtErrorMessage(error);
    }
  };

  const handleDialogOpenToggle = (shouldOpen: boolean) => {
    setOpen(shouldOpen);
    if (shouldOpen) {
      setSelectedTags([]); // Clear tags when modal is closed or dismissed.
      form.setValue('name', '');
      if (!selectedThread) {
        /* Clear primary thread too--a 0th thread = not existing. Only do this if we don't already
        have a primary thread passed in as a prop from the dialog's referer. */
        form.setValue('primaryThread', 0);
      }
    }
  };

  const renderTagsForThread = useCallback(
    (thread: IThreadsResult) => {
      const { id: threadId, tags } = thread;
      if (!tags) return null;
      const isRelatedThread = relatedThreadIds.includes(threadId);

      // Only show existing tags that we're already filtering for.
      const intersectingTags = _intersection(tags, selectedTagLabels);

      if (intersectingTags.length > 3) {
        return (
          <div className="flex items-center gap-2">
            {isRelatedThread && <TagBadge>Related Thread</TagBadge>}
            {_map(intersectingTags.slice(0, 3), (tag) => (
              <TagBadge key={tag}>{tag}</TagBadge>
            ))}
            <Tooltip content={intersectingTags.slice(3).join(', ')}>
              <TagBadge>+{intersectingTags.length - 3}</TagBadge>
            </Tooltip>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2">
          {isRelatedThread && <TagBadge>Related Thread</TagBadge>}
          {_map(intersectingTags, (tag) => (
            <TagBadge key={tag}>{tag}</TagBadge>
          ))}
        </div>
      );
    },
    [selectedTags, relatedThreads]
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenToggle}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="w-full lg:w-auto" size={isMobile ? 'sm' : 'default'}>
            <Plus className="mr-2 h-4 w-4" />
            Generate New Summary
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-5/6 overflow-auto md:w-[650px] md:min-w-[650px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>Summarize Thread Group</DialogTitle>
            </DialogHeader>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-4">
                  <FormLabel className="flex items-center">
                    Name <span style={{ color: colors.red }}>&nbsp;*</span>
                  </FormLabel>
                  <FormControl className="col-span-3">
                    <Input
                      className="border-solid"
                      placeholder="Name"
                      autoFocus
                      maxLength={100}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="col-span-3 col-start-2 px-2 text-xs" />
                </FormItem>
              )}
            />

            {!selectedThread && (
              <FormField
                control={form.control}
                name="primaryThread"
                render={({ field }) => (
                  <FormItem className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-4">
                    <FormLabel className="flex items-center">
                      Primary Thread <span style={{ color: colors.red }}>&nbsp;*</span>
                    </FormLabel>
                    <ReactSelect
                      id="threads-select"
                      placeholder="Select a thread"
                      className="col-span-3"
                      isMulti={false}
                      isCreatable={false}
                      options={threadsOptions}
                      onChange={(option: TReactSelectOption) =>
                        field.onChange({ target: { value: +option.value } })
                      }
                      onInputChange={(inputValue: string) => setSearchValue(inputValue)}
                      menuPortalTarget={null}
                      isSearchable
                    />
                    <FormMessage className="col-span-3 col-start-2 px-2 text-xs" />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="templateId"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-4">
                  <FormLabel className="flex items-center">
                    Export Template <span style={{ color: colors.red }}>&nbsp;*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange}>
                    <FormControl className="col-span-3">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {templates?.results.map((template) => (
                        <SelectItem key={template.id} value={`${template.id}`}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="col-span-3 col-start-2 px-2 text-sm text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="threadsGroup"
              render={({ field }) => (
                <FormItem className="grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-4">
                  <FormLabel className="flex items-center">Threads Group</FormLabel>
                  <ReactSelect
                    id="tags-select"
                    className="col-span-3"
                    isCreatable={false}
                    isSearchable
                    options={tagOptions}
                    placeholder="Select tags"
                    onChange={(option) => {
                      setSelectedTags(option);
                      field.onChange({ target: { value: +option.value } });
                    }}
                    menuPortalTarget={null}
                  />
                  <FormMessage className="col-span-3 col-start-2 px-2 text-xs" />
                </FormItem>
              )}
            />

            <Text>Threads Group Summary</Text>
            <Accordion collapsible disabled={!threadsGroupSummaryList.length} type="single">
              <AccordionItem
                value="STUB"
                className="mb-4 max-h-40 w-full overflow-auto rounded-md 
                bg-secondary p-2 md:max-h-none md:overflow-visible"
              >
                <div className="flex w-full items-center justify-between">
                  <AccordionTrigger
                    id="accordion-trigger"
                    className="flex w-full justify-between px-3"
                  >
                    <div className="flex items-center gap-2">
                      Threads
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-2">
                        {threadsGroupSummaryList.length || 0}
                      </div>
                    </div>
                    <ChevronDown
                      className="group-data-[state=open]:text-blue-500 h-4 w-4 shrink-0 transform text-white
                    transition-transform duration-200  duration-300 group-data-[state=open]:rotate-180"
                    />
                  </AccordionTrigger>
                </div>
                <AccordionContent className="w-full md:overflow-auto">
                  <ol className="mt-2 max-h-96 w-full rounded-md bg-dark-1 md:overflow-auto">
                    {_map(threadsGroupSummaryList, (thread, index) => (
                      <GroupSummaryThreadRow
                        key={thread.id}
                        thread={thread}
                        listIndex={index + 1}
                        tagsList={renderTagsForThread(thread)}
                      />
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => setOpen(false)}
                variant="secondary"
                disabled={isLoading}
                className="mt-3 md:mt-0"
              >
                Cancel
              </Button>
              <Button disabled={isLoading}>Export</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTDPDialog;
